import {Form, Input, Select} from 'antd';
import FadeEffect from 'components/animation/fadeEffect';
import deleteIcon from 'images/delete.svg';
import plusIcon from 'images/plus-circle.png';
import {offeringPartyRoles} from 'utils/constants';
import styles from '../Bonds/assetInstrument.module.scss';
import v2 from './instrumentV2.module.scss';
import {FormCard, spans} from './FormCard';
import {ImageUploadField} from './ImageUploadField';

/**
 * The `offering` editor, shared by every instrument that is brought to market
 * through a syndicate — bonds today, IPOs now too.
 *
 * `offering` lives on BaseFinancialInstrumentInput rather than on any concrete
 * input, so the shape is identical across instrument types; only the draft
 * slice each screen writes to differs. Kept as fields plus two helpers so the
 * host screen keeps control of its own form, submit and modals.
 */

// Flat form values → the nested `offering` the base input expects.
export const buildOffering = ({proceedDescription, allocations, parties}) => ({
  ...(parties?.length && {
    parties: parties.map(party => ({
      role: party?.role,
      name: party?.name,
      logo: party?.logo || null,
      website: party?.website || null,
      description: party?.description || null,
    })),
  }),
  useOfProceeds: {
    ...(proceedDescription && {description: proceedDescription}),
    allocations:
      allocations?.map(allocation => ({
        allocationCategory: allocation.allocationCategory,
        percentageValue: parseInt(allocation.percentageValue),
      })) ?? [],
  },
});

// A stored `offering` → the flat values the form reads.
export const offeringToFormValues = offering => ({
  parties:
    offering?.parties?.map(party => ({
      role: party?.role,
      name: party?.name,
      logo: party?.logo,
      website: party?.website,
      description: party?.description,
    })) ?? [],
  allocations:
    offering?.useOfProceeds?.allocations?.map(allocation => ({
      allocationCategory: allocation?.allocationCategory,
      percentageValue: allocation?.percentageValue,
    })) ?? [],
  proceedDescription: offering?.useOfProceeds?.description || null,
});

export const OfferingFields = ({
  partiesDescription = 'Underwriters, arrangers, registrars and issuing houses — each carries its own role',
  proceedsDescription = 'How the money raised will be allocated. Percentages should total 100.',
}) => (
  <>
    <FormCard title="Parties" description={partiesDescription}>
      <Form.List name="parties">
        {(fields, {add, remove}) => (
          <>
            {fields?.map(({key, name}) => (
              <FadeEffect key={key}>
                <div className={v2.repeatRow}>
                  <div className={v2.partyLayout}>
                    <div className={v2.partyLogo}>
                      <Form.Item name={[name, 'logo']} label="Logo">
                        <ImageUploadField label="Upload logo" />
                      </Form.Item>
                    </div>
                    <div className={v2.partyDetails}>
                      <div className={spans.half}>
                        <Form.Item
                          rules={[
                            {required: true, message: 'Role is required'},
                          ]}
                          name={[name, 'role']}
                          label="Role">
                          <Select placeholder="Select a role">
                            {offeringPartyRoles.map(role => (
                              <Select.Option
                                key={role.value}
                                value={role.value}>
                                {role.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                      <div className={spans.half}>
                        <Form.Item
                          rules={[
                            {required: true, message: 'Name is required'},
                          ]}
                          name={[name, 'name']}
                          label="Party Name">
                          <Input />
                        </Form.Item>
                      </div>
                      <div className={spans.full}>
                        <Form.Item
                          rules={[
                            {message: 'Please enter a valid URL', type: 'url'},
                          ]}
                          name={[name, 'website']}
                          label="Website">
                          <Input />
                        </Form.Item>
                      </div>
                      <div className={spans.full}>
                        <Form.Item
                          name={[name, 'description']}
                          label="Description">
                          <Input.TextArea rows={3} />
                        </Form.Item>
                      </div>
                    </div>
                  </div>
                  <div className={styles['delete-icon']}>
                    <img onClick={() => remove(name)} src={deleteIcon} alt="" />
                  </div>
                </div>
              </FadeEffect>
            ))}
            <button type="button" onClick={add} className={v2.addRow}>
              <img src={plusIcon} alt="" />
              <span>Add party</span>
            </button>
          </>
        )}
      </Form.List>
    </FormCard>

    <FormCard title="Use of proceeds" description={proceedsDescription}>
      <Form.Item
        style={{width: 720}}
        name="proceedDescription"
        label="Description">
        <Input.TextArea rows={6} />
      </Form.Item>
      <div>
        <Form.List name="allocations">
          {(fields, {add, remove}) => (
            <>
              {fields?.map(({key, name}) => (
                <FadeEffect key={key}>
                  <div className={v2.repeatRow}>
                    <div className={styles['al-card']}>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        name={[name, 'allocationCategory']}
                        label="Allocation Category">
                        <Input style={{width: 400}} placeholder="Marketing" />
                      </Form.Item>
                      <Form.Item
                        style={{width: 288, marginLeft: 24}}
                        rules={[{required: true, message: 'Required'}]}
                        name={[name, 'percentageValue']}
                        label="Percentage">
                        <Input placeholder="% in value" type="number" />
                      </Form.Item>
                      <div className={styles['delete-icon']}>
                        <img
                          onClick={() => remove(name)}
                          src={deleteIcon}
                          alt=""
                        />
                      </div>
                    </div>
                  </div>
                </FadeEffect>
              ))}
              <button type="button" onClick={add} className={v2.addRow}>
                <img src={plusIcon} alt="" />
                <span>Add category</span>
              </button>
            </>
          )}
        </Form.List>
      </div>
    </FormCard>
  </>
);

export default OfferingFields;
